/*
 * Copyright (c) 2017-2026 Brighter AI Technologies GmbH. All rights reserved.
 * Unauthorized use, copying, reproduction, or distribution of this file, via any medium,
 * is strictly prohibited.
 */

import React from "react";
import { Box, useMediaQuery } from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@material-ui/core";
import {
  DashboardMenuItem,
  MenuItemLink,
  useQuery,
  setSidebarVisibility,
} from "react-admin";
import { useSelector, useDispatch } from "react-redux";
import { createMuiTheme } from "@material-ui/core/styles";
import { ThemeProvider } from "@material-ui/styles";
import MenuIcon from "../commons/MenuIcon";
import ArrowIcon from "../assets/Menu/accordion-menu.svg";
import ApiKeyIcon from "../assets/Menu/api-key.svg";
import DataUsageIcon from "../assets/Menu/data-usage.svg";
import InvoicesIcon from "../assets/Menu/plans.svg";
import DeveloperToolsIcon from "../assets/Menu/developer-tools.svg";
import DocumentationIcon from "../assets/Menu/documentation.svg";
import TutorialsIcon from "../assets/Menu/tutorials.svg";
import HelpAndSupportIcon from "../assets/Menu/help-and-support.svg";
import FaqIcon from "../assets/Menu/faq.svg";
import RocketIcon from "../assets/Menu/rocket.svg";
import ContactUsIcon from "../assets/Menu/contact-us.svg";
import PythonClientIcon from "../assets/Menu/python-client.svg";
import Usage from "../assets/Menu/usage.svg";
import FullRedactionIcon from "../assets/Menu/full-redaction.svg";
import SelectiveRedactionIcon from "../assets/Menu/selective-redaction.svg";
import * as gtmUtils from "../commons/gtmUtils.js";
import theme from "./theme";

const useStyles = makeStyles((theme) => ({
  root: {
    height: "100%",
    margin: 0,
    paddingTop: theme.spacing(5),
    backgroundColor: "#F7F7F7",
  },
  summaryContent: {
    background: "transparent!important",
    width: "100%",
    pointerEvents: "none",
  },
}));

const overrideTheme = createMuiTheme({
  overrides: {
    MuiIconButton: {
      root: {
        padding: "0important",
      },
      edgeEnd: {
        marginRight: "0!important",
      },
    },
    RaMenuItemLink: {
      root: {
        cursor: "pointer!important",
      },
      active: {
        backgroundColor: theme.palette.primary.main,
        pointerEvents: "none",
      },
    },
    MuiAccordion: {
      root: {
        margin: "0!important",
        padding: "0!important",
      },
    },
    MuiAccordionSummary: {
      root: {
        padding: 0,
        minHeight: "0!important",
        pointerEevents: "none!important",
      },
      content: {
        margin: "0!important",
      },
    },
    MuiAccordionDetails: {
      root: {
        backgroundColor: theme.palette.text.lightGray,
        margin: "0!important",
        padding: "0!important",
        display: "flex",
        flexDirection: "column",
      },
    },
  },
});

const Menu = ({ onMenuClick, logout }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"), { noSsr: true });
  const [wasMobile] = React.useState(isMobile);
  const dispatch = useDispatch();
  const open = useSelector((state) => state.admin.ui.sidebarOpen);

  React.useEffect(() => {
    if (isMobile !== wasMobile) {
      dispatch(setSidebarVisibility(false));
    }
  }, [isMobile, wasMobile, dispatch]);

  const { data } = useQuery({
    type: "getOne",
    resource: "api-keys",
    payload: { id: "" },
  });
  const keyValue = data ? "?subscription_key=" + data["primaryKey"] : "";
  const externalSRALink = "https://portal.brighter.ai/sra/" + keyValue;
  const externalDOCLink = "https://docs.brighter.ai/docs";
  const externalDocPython = "https://github.com/brighter-ai/redact-client";

  const linkSRA = () => {
    gtmUtils.dispatchGtmEvent("user_interaction", "external_link_sra");
    window.open(externalSRALink, "_blank");
  };
  const linkDOC = () => {
    gtmUtils.dispatchGtmEvent("user_interaction", "external_link_doc");
    window.open(externalDOCLink, "_blank");
  };
  const linkDOCPython = () => {
    gtmUtils.dispatchGtmEvent("user_interaction", "external_link_doc");
    window.open(externalDocPython, "_blank");
  };

  return (
    <ThemeProvider theme={overrideTheme}>
      <Box className={classes.root} mt={1}>
        <DashboardMenuItem
          onClick={onMenuClick}
          sidebarIsOpen={false}
          leftIcon={
            <MenuIcon src={FullRedactionIcon} alt="Full Redaction Link" />
          }
          primaryText={"Full Redaction"}
        />
        <MenuItemLink
          to=""
          isActive={() => false}
          primaryText={"Selective Redaction"}
          leftIcon={
            <MenuIcon
              src={SelectiveRedactionIcon}
              alt="Selective Redaction Link"
            />
          }
          onClick={linkSRA}
          sidebarIsOpen={open}
        />
        <MenuItemLink
          to="/plans"
          primaryText={"Plans"}
          leftIcon={<MenuIcon src={RocketIcon} alt="Plans Link" />}
          onClick={onMenuClick}
          sidebarIsOpen={open}
        />
        <Accordion>
          <AccordionSummary
            expandIcon={
              <MenuIcon src={ArrowIcon} alt="Arrow icon brighter AI" />
            }
          >
            <MenuItemLink
              className={classes.summaryContent}
              to=""
              primaryText={"Usage"}
              isActive={() => false}
              leftIcon={<MenuIcon src={Usage} alt="Usage accordion menu" />}
              sidebarIsOpen={open}
            />
          </AccordionSummary>
          <AccordionDetails>
            <MenuItemLink
              to="/usage"
              primaryText={"Data usage"}
              leftIcon={<MenuIcon src={DataUsageIcon} alt="Data usage Link" />}
              sidebarIsOpen={open}
            />
            <MenuItemLink
              to="/invoices"
              primaryText={"Invoices"}
              leftIcon={<MenuIcon src={InvoicesIcon} alt="Invoices Link" />}
              sidebarIsOpen={open}
            />
          </AccordionDetails>
        </Accordion>
        <Accordion>
          <AccordionSummary
            expandIcon={
              <MenuIcon src={ArrowIcon} alt="Arrow icon brighter AI" />
            }
          >
            <MenuItemLink
              className={classes.summaryContent}
              to=""
              primaryText={"Developer tools"}
              isActive={() => false}
              leftIcon={
                <MenuIcon
                  src={DeveloperToolsIcon}
                  alt="Developer tools accordion menu"
                />
              }
              sidebarIsOpen={open}
            />
          </AccordionSummary>
          <AccordionDetails>
            <MenuItemLink
              to="/api-keys"
              primaryText={"API key"}
              leftIcon={<MenuIcon src={ApiKeyIcon} alt="Api Key Link" />}
              onClick={onMenuClick}
              sidebarIsOpen={open}
            />
            <MenuItemLink
              to=""
              isActive={() => false}
              primaryText={"API Documentation"}
              leftIcon={
                <MenuIcon
                  src={DocumentationIcon}
                  alt="API Documentation Link"
                />
              }
              onClick={linkDOC}
              sidebarIsOpen={open}
            />
            <MenuItemLink
              to=""
              isActive={() => false}
              primaryText={"Python client"}
              leftIcon={
                <MenuIcon src={PythonClientIcon} alt="Python client Link" />
              }
              onClick={linkDOCPython}
              sidebarIsOpen={open}
            />
          </AccordionDetails>
        </Accordion>
        <Accordion>
          <AccordionSummary
            expandIcon={
              <MenuIcon src={ArrowIcon} alt="Arrow icon brighter AI" />
            }
          >
            <MenuItemLink
              className={classes.summaryContent}
              to=""
              primaryText={"Help & Support"}
              isActive={() => false}
              leftIcon={
                <MenuIcon
                  src={HelpAndSupportIcon}
                  alt="Help and support accordion menu"
                />
              }
              sidebarIsOpen={open}
            />
          </AccordionSummary>
          <AccordionDetails>
            <MenuItemLink
              to="/faq"
              primaryText={"FAQ"}
              leftIcon={<MenuIcon src={FaqIcon} alt="Faq Link" />}
              sidebarIsOpen={open}
            />
            <MenuItemLink
              to="/tutorials"
              primaryText={"Tutorials"}
              leftIcon={<MenuIcon src={TutorialsIcon} alt="Tutorials Link" />}
              sidebarIsOpen={open}
            />
            <MenuItemLink
              to="/contact"
              primaryText={"Contact Us"}
              leftIcon={<MenuIcon src={ContactUsIcon} alt="Contact Us Link" />}
              sidebarIsOpen={open}
            />
          </AccordionDetails>
        </Accordion>
        {isMobile && logout}
      </Box>
    </ThemeProvider>
  );
};

export default Menu;
